import { createSlice } from "@reduxjs/toolkit";
import {
  createPayment,
  fetchPaymentsByInvoice,
  fetchInvoicePaymentsByAppointmentId,
  fetchAllPayments,
  fetchDeposits,
  collectDepositPayment,
  refundDeposit,
} from "../thunks/paymentsThunk";

const paymentsSlice = createSlice({
  name: "payments",
  initialState: {
    payments: [],
    deposits: [],
    status: "idle",
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalPayments: 0,
    invoicePayments: [],
    appointmentPayments: [],
    currentPayment: [],
  },
  reducers: {
    clearAppointmentPayments: (state) => {
      state.appointmentPayments = [];
    },
    setCurrentPayment: (state, action) => {
      state.currentPayment = action.payload;
    },
    clearInvoicePayments: (state) => {
      state.invoicePayments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle payment creation
      .addCase(createPayment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        console.log('Current state:', state);
        state.status = "succeeded";
        if (!state.payments) {
          state.payments = [];
        }
        state.payments.push(action.payload);
        if (!state.appointmentPayments) {
          state.appointmentPayments = [];
        }
        state.appointmentPayments.push(action.payload);
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Handle fetching payments by invoice
      .addCase(fetchPaymentsByInvoice.pending, (state) => {
        state.status = "loading";
        state.invoicePayments = [];
        state.error = null;
      })
      .addCase(fetchPaymentsByInvoice.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.invoicePayments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPaymentsByInvoice.rejected, (state, action) => {
        state.status = "failed";
        state.invoicePayments = [];
        state.error = action.payload;
      })
      .addCase(fetchInvoicePaymentsByAppointmentId.pending, (state) => {
        state.status = "loading";
        state.appointmentPayments = [];
        state.error = null;
      })
      .addCase(fetchInvoicePaymentsByAppointmentId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.appointmentPayments = Array.isArray(action.payload?.payments) ? action.payload.payments : [];
      })
      .addCase(fetchInvoicePaymentsByAppointmentId.rejected, (state, action) => {
        state.status = "failed";
        state.appointmentPayments = [];
        state.error = action.payload;
      })
      .addCase(fetchAllPayments.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllPayments.fulfilled, (state, action) => {
        console.log("payload", action.payload);
        state.status = "succeeded";
        state.payments = action.payload.payments;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.totalPayments = action.payload.totalPayments;
      })
      .addCase(fetchAllPayments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Handle fetching deposits
      .addCase(fetchDeposits.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDeposits.fulfilled, (state, action) => {
        state.status = "succeeded";
        const newDeposits = action.payload.deposits || [];
        const clientId = action.meta.arg; // Get the clientId from the thunk argument

        // Add clientId to each deposit and merge with existing deposits
        const depositsWithClientId = newDeposits.map(deposit => ({
          ...deposit,
          clientId: clientId
        }));

        // Remove any existing deposits for this client to avoid duplicates
        const filteredExistingDeposits = (state.deposits || []).filter(
          deposit => deposit.clientId !== clientId
        );

        // Combine existing deposits (from other clients) with new deposits
        state.deposits = [...filteredExistingDeposits, ...depositsWithClientId];
      })
      .addCase(fetchDeposits.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Handle collecting deposit payment
      .addCase(collectDepositPayment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(collectDepositPayment.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Add the new deposit to the deposits array
        if (action.payload.payment && action.payload.payment.deposit) {
          if (!state.deposits) {
            state.deposits = [];
          }
          state.deposits.push(action.payload.payment);
        }
      })
      .addCase(collectDepositPayment.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Handle deposit refund
      .addCase(refundDeposit.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(refundDeposit.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Update the specific deposit in the deposits array
        if (action.payload.deposit && state.deposits) {
          const index = state.deposits.findIndex(deposit => deposit._id === action.payload.deposit._id);
          if (index !== -1) {
            state.deposits[index] = action.payload.deposit;
          }
        }
      })
      .addCase(refundDeposit.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearAppointmentPayments, setCurrentPayment, clearInvoicePayments } = paymentsSlice.actions;
export default paymentsSlice.reducer;
